import type {
  Block, Cell, ChipCell, Column, NoteBlock, Tone,
} from '@/kova/spec/types'

import { ApiError } from '@/lib/api/errors'

import type { ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Onboarding — the working set, the evidence queue, the institution's queue and
 * the re-verification schedule (go-wallet#770, #825; this console #138).
 *
 *   onb-queue     GET /v1/admin/onboarding/applications
 *   onb-evidence  GET /v1/admin/onboarding/evidence
 *   onb-emi       GET /v1/admin/onboarding/institution-acceptance
 *   onb-reverify  GET /v1/admin/onboarding/reverification-due
 *
 * # One module, because these four are one shift and must not fork a vocabulary
 *
 * The backend built the first three behind ONE seam on purpose: `waiting_on`
 * and `blocking` come from a single pair of pure functions shared with the SLA
 * queue, so that one customer cannot read as waiting on the institution on one
 * screen and on the customer on another. That divergence is invisible in review,
 * appears only when somebody has two screens open, and cannot be fixed
 * afterwards without first deciding which screen was lying. Two adapters
 * normalising those words independently would reintroduce it on this side of
 * the wire, so the labels live here once.
 *
 * # This is NOT the SLA breach queue, and that mistake has been made four times
 *
 * `GET /onboarding/sla` — which `onb-sla` already reads — is scoped to
 * `sla_due_at <= now() + horizon`. An application two hours into a forty-eight
 * hour target is absent from it and a terminal one can never appear. The
 * applications read is the WORKING SET: every open application including the
 * ones that are fine, because an operator looking for a customer looks for the
 * customer and not for their state.
 *
 * # Two states exist and the screen draws three
 *
 * `our_state` is the identity vendor's own word for the session — null when the
 * customer has started none, which is a POSITION rather than a missing value.
 * `acceptance` is the INSTITUTION's verdict, mirrored; migration 001's comment
 * on the column reads "The institution owns final acceptance. This column
 * mirrors its verdict and is never set by us on our own authority."
 *
 * The designed third column is that same column under another name, and it is
 * NOT synthesised here: collapsing the two is precisely what the screen exists
 * to prevent. The corpus's values — `submitted`, `evidence_requested`,
 * `refused`, `emi_acceptance`, `retry` — are a third vocabulary that no column
 * in this database can hold, and the endpoint 400s on each by name.
 *
 * # What else is dropped, per `./aml.ts`
 *
 *   - The SLA BAR and the Age column on `onb-queue`. A percent needs a target
 *     and an elapsed figure; this read serves a due instant and a
 *     state-entered instant and no duration. Dividing them out against the
 *     browser's clock would be this console computing compliance posture on a
 *     compliance screen, disagreeing with `onb-sla`, whose postures are
 *     server-computed. The instants render as instants.
 *   - The SHA-256 column on `onb-evidence`. `identifier_hash` is a keyed HMAC
 *     under a key the database does not hold, and its UNKEYED predecessor was
 *     removed by go-wallet#363 after a passport number was recovered from a
 *     live-shaped table in 0.49 seconds — a digest of a nine-character
 *     identifier from a known issuer is an index, not an anonymisation. What is
 *     served instead is whether the vendor supplied an identifier at all and
 *     whether it predates a key rotation, which are the two questions an
 *     operator has. There is no value here, and no masked value with a reveal
 *     toggle either (law 11).
 *   - The "Our IDV" column on `onb-emi`. There is no `our_state` on that
 *     response; it is on the sibling read, and joining the two here would be
 *     this console inventing a join and then owning the disagreement when both
 *     pages are open.
 *   - The Requested column and the "If overdue" consequence on `onb-reverify`.
 *     `requested` is false on every row BY CONSTRUCTION — nothing in this build
 *     asks anybody to re-verify — so it distinguishes nobody, and drawn per row
 *     it shows an outreach process that does not exist. "restricted
 *     (receive-only)" is not policy-backed: no row ties an overdue
 *     re-verification to an account state, a restriction is an instruction
 *     through the authorisation seam, and a freeze is the institution's alone
 *     (law 1).
 *
 * # There is no money on any of these four screens
 *
 * No amount, no currency, no balance is on any of these wires. So nothing here
 * imports a formatter and nothing here builds an amount cell — there is nothing
 * to format, and a formatter in the file would be an invitation.
 *
 * # Read-only, and there is no approve control at any permission
 *
 * The backend's own words: "THERE IS NO SUCH POWER IN THE MANDATE." We are a
 * registered agent; the institution accepts or rejects and this backend mirrors
 * the verdict. Nothing on this surface accepts, rejects, verifies evidence,
 * advances an application, requests a re-verification, defers one or restricts
 * an account — and a console that rendered one of those would be rendering a
 * control the backend must not expose (law 1).
 */

// ---------------------------------------------------------------------------
// Paths
// ---------------------------------------------------------------------------

export const APPLICATIONS_PATH = '/onboarding/applications'
export const EVIDENCE_PATH = '/onboarding/evidence'
export const ACCEPTANCE_PATH = '/onboarding/institution-acceptance'
export const REVERIFICATION_PATH = '/onboarding/reverification-due'

const APPLICATIONS_SOURCE = 'postgres · customers (GET /v1/admin/onboarding/applications)'
const EVIDENCE_SOURCE = 'postgres · onboarding_evidence (GET /v1/admin/onboarding/evidence)'
const ACCEPTANCE_SOURCE =
  'postgres · customers + audit_log (GET /v1/admin/onboarding/institution-acceptance)'
const REVERIFICATION_SOURCE =
  'postgres · reverification schedule (GET /v1/admin/onboarding/reverification-due)'

/**
 * Our operational record — of an application's position, not of its outcome.
 *
 * `acceptance` is the one field on these screens that is NOT ours: it mirrors
 * the institution's verdict and we never set it on our own authority. The
 * badge says so rather than letting `operational` imply we decided any of it.
 */
const AUTHORITY = 'operational — acceptance mirrors the institution’s verdict, never ours'

// ---------------------------------------------------------------------------
// Reading the wire, fail-closed
// ---------------------------------------------------------------------------

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null
}

function asArray(raw: unknown): readonly unknown[] | null {
  return Array.isArray(raw) ? (raw as readonly unknown[]) : null
}

function readText(o: Record<string, unknown>, key: string): string | null {
  const raw = o[key]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readBool(o: Record<string, unknown>, key: string): boolean | null {
  const raw = o[key]
  return typeof raw === 'boolean' ? raw : null
}

/**
 * `word in TABLE` walks the prototype chain, so `toString` and `__proto__`
 * answer true against any object literal and the lookup returns a FUNCTION
 * rather than a tone. An unrecognised state would come back "recognised",
 * record no gap, and leave the screen badging itself live over a word it does
 * not know.
 */
function knows(table: Readonly<Record<string, unknown>>, word: string): boolean {
  return Object.prototype.hasOwnProperty.call(table, word)
}

type IntRead =
  | { readonly kind: 'exact'; readonly value: number }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * Three states, not two. A `customer_id` past `Number.MAX_SAFE_INTEGER` has
 * already lost digits in `JSON.parse` — printing it shows an id wrong in its
 * last places and entirely plausible, which is how an operator opens the wrong
 * person's application.
 */
function readInt(o: Record<string, unknown>, key: string): IntRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) return { kind: 'unreadable' }
  return { kind: 'exact', value: raw }
}

type InstantRead =
  | { readonly kind: 'at'; readonly label: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unreadable' }

/**
 * The epoch guard is not paranoia: several of these fields are plain
 * `time.Time` with no `omitempty`, so a zero value reaches the wire as
 * `0001-01-01T00:00:00Z` and parses into a date in the year 1 — which renders
 * as a timestamp rather than as an absence.
 */
function readInstant(o: Record<string, unknown>, key: string): InstantRead {
  const raw = o[key]
  if (raw === undefined || raw === null) return { kind: 'absent' }
  if (typeof raw !== 'string' || raw === '') return { kind: 'unreadable' }
  const at = new Date(raw)
  if (Number.isNaN(at.getTime()) || at.getTime() <= 0) return { kind: 'unreadable' }
  return { kind: 'at', label: readAtStamp(at) }
}

/** Groups repeated gaps so the provenance chip stays readable at 200 rows. */
function summarise(gaps: readonly string[]): string[] {
  const counts = new Map<string, number>()
  for (const gap of gaps) counts.set(gap, (counts.get(gap) ?? 0) + 1)
  return [...counts].map(([label, n]) =>
    `${label} (${String(n)} ${n === 1 ? 'row' : 'rows'})`)
}

// ---------------------------------------------------------------------------
// The vocabulary, shared so the four screens cannot disagree
// ---------------------------------------------------------------------------

/**
 * The acceptance states, from `internal/onboarding`.
 *
 * A state added by a migration renders with the value the backend sent and an
 * attention tone — the less confident reading. A neutral chip would make a
 * state nobody has taught this console about look like business as usual.
 *
 * `accepted` is green and `rejected` is neutral rather than red: a rejection is
 * a completed decision the institution made, not an incident on our side, and
 * red on it would teach operators that red means "somebody was refused".
 */
const ACCEPTANCE_TONE: Readonly<Record<string, Tone>> = {
  pending: 'n',
  in_review: 'a',
  more_info_required: 'a',
  accepted: 'g',
  rejected: 'n',
}

/**
 * The identity vendor's own session states.
 *
 * `declined` here is a REAL value and is a near-collision with the acceptance
 * enum's `rejected`, which is why the two vocabularies are separate tables:
 * folding them would let a vendor decline read as an institution rejection.
 */
const IDV_TONE: Readonly<Record<string, Tone>> = {
  pending: 'n',
  in_progress: 'n',
  approved: 'g',
  declined: 'r',
  retry: 'a',
  expired: 'a',
  abandoned: 'a',
}

const WAITING_LABEL: Readonly<Record<string, string>> = {
  customer: 'the customer',
  institution: 'the institution',
  nobody: 'nobody',
}

/** Who owns the next action, from the backend's own derivation and never ours. */
function waitingCell(o: Record<string, unknown>, gaps: string[]): ChipCell {
  const raw = readText(o, 'waiting_on')
  if (raw === null || !knows(WAITING_LABEL, raw)) {
    gaps.push('a party this console recognises in waiting_on')
    return [raw ?? 'unstated', 'a']
  }
  // Waiting on nobody is neutral, not green: it means nothing is owed, which on
  // a terminal application is correct and on an open one is the finding.
  return [WAITING_LABEL[raw] ?? raw, 'n']
}

/**
 * The institution's verdict — mirrored, never ours.
 *
 * Labelled `acceptance` rather than `state` everywhere it is drawn, because the
 * one thing this column must not become is the screen's single answer to "where
 * is this application".
 */
function acceptanceCell(o: Record<string, unknown>, gaps: string[]): ChipCell {
  const raw = readText(o, 'acceptance')
  if (raw === null) {
    gaps.push('an acceptance state')
    return ['unstated', 'a']
  }
  if (!knows(ACCEPTANCE_TONE, raw)) {
    gaps.push('an acceptance state this console recognises')
    return [raw, 'a']
  }
  return [raw, ACCEPTANCE_TONE[raw] ?? 'a']
}

/**
 * The customer, as this wire can identify them.
 *
 * `legal_name` IS on these responses — unlike the SLA queue, which carries only
 * an id — so the subtitle carries the id and the country an operator needs to
 * disambiguate two people with one name.
 */
function customerCell(o: Record<string, unknown>, gaps: string[]): Cell {
  const id = readInt(o, 'customer_id')
  if (id.kind !== 'exact') gaps.push('a customer id')
  const name = readText(o, 'legal_name')
  if (name === null) gaps.push('a legal name')
  const country = readText(o, 'country_code')
  const idLabel = id.kind === 'exact' ? String(id.value) : '— id unreadable'
  return [name ?? '— unnamed', country === null ? idLabel : `${idLabel} · ${country}`]
}

// ---------------------------------------------------------------------------
// The read, resolved into an answer either way
// ---------------------------------------------------------------------------

export type Attempt =
  | { readonly ok: true; readonly payload: unknown }
  | {
      readonly ok: false
      readonly reason: string
      readonly decisionRef: string | null
      readonly refused: boolean
    }

async function attempt(read: () => Promise<unknown>, subject: string): Promise<Attempt> {
  try {
    return { ok: true, payload: await read() }
  } catch (error) {
    if (error instanceof ApiError) {
      return {
        ok: false,
        reason: `${subject} answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        decisionRef: error.decisionRef,
        refused: error.isForbidden,
      }
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return { ok: false, reason: `${subject} could not be reached.`, decisionRef: null, refused: false }
  }
}

/** Law 13 — a refusal with no reference says so rather than inventing one. */
function refLine(failed: Extract<Attempt, { ok: false }>, fallback: string): string {
  if (failed.decisionRef !== null) return `decision_ref ${failed.decisionRef}`
  if (failed.refused) {
    return 'The refusal arrived without a decision_ref, so there is nothing to quote in an escalation.'
  }
  return fallback
}

function unread(
  kicker: string, title: string, body: string, failed: Extract<Attempt, { ok: false }>,
  source: string,
): ScreenData {
  const note: NoteBlock = {
    t: 'note', tone: 'r', kicker, title,
    body: `${failed.reason} ${body}`,
    ref: refLine(failed, 'Nothing on this screen was read.'),
  }
  return {
    blocks: [note],
    provenance: { kind: 'unread', source, reason: failed.reason },
  }
}

/** The stamp. Neither route says when it read its rows, so this is a receipt. */
function receipt(at: Date): string {
  return `${readAtStamp(at)} (console receipt — the endpoint does not state when it read)`
}

/**
 * The backend's own disclosure sentence, as the backend wrote it.
 *
 * # Why this is an extract rather than the whole field
 *
 * Each of these responses carries a required `*_note` written by the handler
 * that knows what its own schema cannot say, and the instruction is to render
 * it AS WRITTEN rather than to paraphrase — a paraphrase drifts in the
 * dangerous direction, turning a permanent property of the schema into
 * something that reads as a temporary gap.
 *
 * It cannot be rendered whole. `withoutRoadmapNotes` drops any note body over
 * 220 characters at `readScreen` (#120), and every one of these fields is
 * several times that, so a verbatim block would render NOTHING at all — which
 * is worse than an extract, because the operator is then told nothing. The
 * block language has no other home for a paragraph either: `BlockHead.note` is
 * a right-aligned one-line strip and `foot2` is stripped by the same rule.
 *
 * So what is rendered is the field's OWN FIRST SENTENCE, verbatim, off the
 * wire, with the field named in `ref` so an operator can see there is more and
 * where it came from. These handlers front-load the load-bearing clause —
 * "`requested` IS FALSE ON EVERY ROW BY CONSTRUCTION", "`overdue` MEANS THE
 * DATE HAS PASSED AND NOTHING ELSE" — so the first sentence is the one that
 * changes what somebody does.
 *
 * Nothing is emitted when the backend did not send the field. This console
 * must never state a sentence of its own as the backend's.
 */
function disclosure(
  o: Record<string, unknown>, key: string, kicker: string, title: string, tone: Tone,
): NoteBlock | null {
  const full = readText(o, key)
  if (full === null) return null

  // The first sentence, on the terminal period — not on any period, so
  // `last_computed_at.` and `#363.` do not cut it early.
  const match = /^[\s\S]*?[.](?=\s|$)/.exec(full.trim())
  let body = (match === null ? full.trim() : match[0]).trim()
  if (body.length > MAX_DISCLOSURE) {
    // A sentence that still does not fit is cut at a word boundary and marked,
    // so it reads as an extract of something longer rather than as the whole
    // statement. The alternative is the filter dropping it silently.
    const cut = body.slice(0, MAX_DISCLOSURE).lastIndexOf(' ')
    body = `${body.slice(0, cut > 0 ? cut : MAX_DISCLOSURE).trimEnd()}…`
  }
  return { t: 'note', tone, kicker, title, body, ref: `${key}, as the backend wrote it` }
}

/**
 * The longest body `withoutRoadmapNotes` will let through, less a margin for
 * the ellipsis. Kept here rather than imported so this module does not depend
 * on the filter's internals; the test that fails if they diverge is the one
 * asserting these notes actually survive a round trip through `readScreen`.
 */
const MAX_DISCLOSURE = 210

// ===========================================================================
// onb-queue — the working set
// ===========================================================================

/**
 * The columns, from what the endpoint can actually answer.
 *
 * The corpus draws Customer / Kind / State / verified_by_idv / accepted_by_emi
 * / SLA / Age. `State` is gone because it is `accepted_by_emi` under another
 * name and synthesising it is what this screen exists to prevent; the SLA bar
 * and Age are gone because neither is a figure this read sends.
 *
 * `Waiting on`, `Blocking` and `Evidence pending` are new and are the point of
 * the endpoint: whether chasing will help, what the row is waiting for in the
 * backend's own wording, and how many documents are outstanding.
 */
const QUEUE_COLS: readonly Column[] = [
  ['Customer', 's', 'minmax(180px,1.4fr)'],
  ['Kind', 'c', '104px'],
  ['Identity check (ours)', 'c', '150px'],
  ['Acceptance (institution)', 'c', '170px'],
  ['Waiting on', 'c', '128px'],
  ['Enhanced', 'c', '104px'],
  ['Evidence pending', 'n', '134px'],
  ['In state since', 'm', '150px'],
  ['SLA due', 'm', '150px'],
  ['Blocking', 'x', 'minmax(200px,1.4fr)'],
]

interface RowRead {
  readonly cells: readonly Cell[]
  readonly gaps: readonly string[]
}

function readApplication(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const customer = customerCell(o, gaps)

  const kind = readText(o, 'kind')
  if (kind === null) gaps.push('an applicant kind')

  // `our_state` is a POINTER on the wire and null means the customer has
  // started no verification session at all. That is a position, not a missing
  // value, and it renders as one — a dash would say we failed to read it.
  const ourRaw = readText(o, 'our_state')
  const ourCell: ChipCell = ourRaw === null
    ? ['none started', 'n']
    : knows(IDV_TONE, ourRaw)
      ? [ourRaw, IDV_TONE[ourRaw] ?? 'a']
      : [ourRaw, 'a']
  if (ourRaw !== null && !knows(IDV_TONE, ourRaw)) {
    gaps.push('an identity-check state this console recognises')
  }

  const theirs = acceptanceCell(o, gaps)
  const waiting = waitingCell(o, gaps)

  const enhanced = readBool(o, 'enhanced')
  if (enhanced === null) gaps.push('whether enhanced diligence applies')
  const enhancedCell: ChipCell = enhanced === null
    ? ['unstated', 'a']
    : enhanced ? ['EDD', 'a'] : ['standard', 'n']

  // A real zero: nobody has anything outstanding. Distinct from an unread one,
  // which is a dash — "no documents outstanding" and "we do not know how many
  // are outstanding" send an operator to opposite places.
  const pending = readInt(o, 'evidence_pending')
  if (pending.kind !== 'exact') gaps.push('a pending-evidence count')

  const entered = readInstant(o, 'state_entered_at')
  if (entered.kind !== 'at') gaps.push('when the current state began')

  // Absent `sla_due_at` is a FACT and not a gap: the backend uses a pointer
  // precisely so "no target applies" is expressible. It reads as that, never
  // as a dash.
  const due = readInstant(o, 'sla_due_at')
  if (due.kind === 'unreadable') gaps.push('an SLA due date')

  const blocking = readText(o, 'blocking')
  if (blocking === null) gaps.push('the sentence saying what the row waits for')

  return {
    cells: [
      customer,
      kind === null ? ['—', 'a'] : [kind, 'n'],
      ourCell,
      theirs,
      waiting,
      enhancedCell,
      pending.kind === 'exact' ? String(pending.value) : '—',
      entered.kind === 'at' ? entered.label : '—',
      due.kind === 'at' ? due.label : due.kind === 'absent' ? 'no target' : '—',
      blocking ?? '—',
    ],
    gaps,
  }
}

const COUNT_COLS: readonly Column[] = [
  ['Acceptance state', 'c', '190px'],
  ['Applications', 'n', '120px'],
]

/**
 * The population, over the WHOLE table and never over this page.
 *
 * Never from `onboarding_sla_breaches` either: that table holds only rows that
 * have already breached and is only as fresh as the last detector run, so a
 * count taken from it is a biased sample presented as a population. Every state
 * appears including the ones at zero — a state missing from a count block reads
 * as a state nobody is in, which is a different claim from nobody being in it.
 */
function countsTable(raw: unknown, gaps: string[]): Block {
  const counts = asRecord(raw)
  const rows: (readonly Cell[])[] = []
  if (counts === null) {
    gaps.push('the per-state population counts')
  } else {
    for (const [state, value] of Object.entries(counts)) {
      const n = typeof value === 'number' && Number.isSafeInteger(value) ? value : null
      if (n === null) gaps.push('a population count this console could not state')
      rows.push([
        knows(ACCEPTANCE_TONE, state) ? [state, ACCEPTANCE_TONE[state] ?? 'a'] : [state, 'a'],
        n === null ? '—' : String(n),
      ])
    }
  }
  return {
    t: 'table', title: 'Every application, by acceptance state', src: 'ledger', facets: false,
    cols: COUNT_COLS, rows,
    footer: rows.length === 0
      ? 'The population could not be read. This is not a table with nobody in it.'
      : 'Over the whole customers table, including the states at zero — not over the page above, and not from the breach table.',
  }
}

const QUEUE_MISSING: readonly string[] = [
  'a single funnel state — two states exist and the third column the design draws is the institution’s verdict under another name; it is not synthesised here',
  'the SLA progress bar and an age — this read sends a due instant and a state-entered instant and no elapsed figure; SLA posture is server-computed on SLA breaches',
]

export function composeApplicationQueue(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Application queue', 'The application queue could not be read',
      'No applicant, state or owner below is known — that is different from every application having reached a terminal state.',
      read, APPLICATIONS_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const apps = o === null ? null : asArray(o['applications'])
  if (o === null || apps === null) {
    return unread(
      'Application queue', 'The application queue could not be read',
      'The queue returned a shape this console does not know.',
      { ok: false, reason: 'The queue returned a shape this console does not know.', decisionRef: null, refused: false },
      APPLICATIONS_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  for (const raw of apps) {
    const row = readApplication(raw)
    if (row === null) unreadable += 1
    else { rows.push(row.cells); gaps.push(...row.gaps) }
  }

  const hasMore = readBool(o, 'has_more') === true
  // A `waiting_on` filter runs AFTER the query on the backend and can return a
  // short page, so a console reading a short page as the end of the queue would
  // stop paging early. Not applied here — but said when the backend reports it.
  const filtered = readText(o, 'filtered_in_go')

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No application is in flight. Every one has reached a terminal state, and this was read to confirm it.'
        : `No row on this page could be read: ${String(unreadable)} came back in a shape this console does not know. This is an unread queue, not an empty one.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'application' : 'applications'} shown${hasMore ? ' · the backend caps the page, so this is not the whole queue' : ''}${filtered === null ? '' : ` · narrowed to waiting on ${filtered}, which the backend filters after the query`}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read at all`}`

  const blocks: Block[] = [
    {
      t: 'table',
      title: 'Applications in flight',
      src: 'ledger',
      // The corpus draws `state=in_review`, `jurisdiction=DE`, `edd=false`,
      // `sla=at_risk` as applied. This read applies no filter, and a facet bar
      // is a claim that one is applied.
      facets: false,
      cols: QUEUE_COLS,
      rows,
      footer,
    },
    countsTable(o['counts'], gaps),
  ]

  const missing = [...QUEUE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} application row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: APPLICATIONS_SOURCE, authority: AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Application queue',
    subject: `customers · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

// ===========================================================================
// onb-evidence — references only; the documents stay at the vendor
// ===========================================================================

/**
 * The four values `evidence_status` can hold, read from the enum rather than
 * from the design. `submitted` and `retry` are corpus inventions and a
 * `?status=submitted` is a 400.
 */
const EVIDENCE_TONE: Readonly<Record<string, Tone>> = {
  pending: 'a',
  verified: 'g',
  rejected: 'r',
  expired: 'a',
}

/**
 * No SHA-256 column, and nothing that could become one.
 *
 * `Identifier` carries PRESENCE and key version — whether the vendor supplied
 * one at all, and whether it predates a rotation. There is no cell anywhere on
 * this screen that holds a digest, a partial digest or a masked value with a
 * reveal (law 11).
 */
const EVIDENCE_COLS: readonly Column[] = [
  ['ID', 'm', '86px'],
  ['Customer', 's', 'minmax(170px,1.3fr)'],
  ['Evidence type', 'x', '170px'],
  ['Status', 'c', '124px'],
  ['Vendor', 'x', '110px'],
  ['Vendor reference', 'm', 'minmax(170px,1.2fr)'],
  ['Identifier', 'c', '160px'],
  ['Issuing country', 'x', '128px'],
  ['Expires', 'm', '150px'],
  ['Standing', 'c', '150px'],
]

/**
 * Whether the vendor supplied an identifier, and under which key.
 *
 * Never the value. `hash_key_version` is served so an operator can tell a row
 * that predates a key rotation from one that does not, which is the only thing
 * the version is for.
 */
function identifierCell(o: Record<string, unknown>, gaps: string[]): ChipCell {
  const present = readBool(o, 'identifier_hash_present')
  if (present === null) {
    gaps.push('whether the vendor supplied an identifier')
    return ['unstated', 'a']
  }
  if (!present) return ['none supplied', 'a']
  const version = readInt(o, 'hash_key_version')
  if (version.kind !== 'exact') {
    gaps.push('the key version an identifier was taken under')
    return ['supplied', 'n']
  }
  return [`supplied · key v${String(version.value)}`, 'n']
}

/**
 * `superseded` is the finding, not a detail, and redaction is the other one.
 *
 * A `pending` row that a later row has already carried to `verified` is the
 * backlog's arithmetic rather than its workload: nothing disposes of an
 * evidence row, and each vendor delivery INSERTs a new one, so the queue grows
 * monotonically. A large queue here means "nothing can dispose of these", which
 * has the opposite remedy from "the team is behind".
 *
 * A redacted row is RETURNED and marked rather than omitted: a queue that
 * dropped erased rows would read as a smaller backlog than it is.
 */
function standingCell(o: Record<string, unknown>, gaps: string[]): ChipCell {
  const redacted = readBool(o, 'redacted')
  if (redacted === null) gaps.push('whether an evidence row has been erased')
  const superseded = readBool(o, 'superseded')
  if (superseded === null) gaps.push('whether an evidence row has been superseded')

  if (redacted === true) return ['erased under a privacy request', 'a']
  if (superseded === true) return ['superseded by a later row', 'a']
  if (redacted === null || superseded === null) return ['partly read', 'a']
  return ['current', 'n']
}

function readEvidence(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const id = readInt(o, 'id')
  if (id.kind !== 'exact') gaps.push('an evidence id')

  const customer = customerCell(o, gaps)

  const type = readText(o, 'evidence_type')
  if (type === null) gaps.push('an evidence type')

  const status = readText(o, 'status')
  if (status === null) gaps.push('an evidence status')
  const statusCell: ChipCell = status === null
    ? ['unstated', 'a']
    : knows(EVIDENCE_TONE, status)
      ? [status, EVIDENCE_TONE[status] ?? 'a']
      : [status, 'a']
  if (status !== null && !knows(EVIDENCE_TONE, status)) {
    gaps.push('an evidence status this console recognises')
  }

  // The vendor trace is legitimate on this surface and deliberately absent from
  // the customer one, so a frontend never learns the vendor's identity.
  const provider = readText(o, 'provider')
  if (provider === null) gaps.push('a vendor name')
  const providerRef = readText(o, 'provider_ref')
  if (providerRef === null) gaps.push('a vendor reference')

  const issuing = readText(o, 'issuing_country')

  // Absent `expires_at` is a fact: most evidence states no expiry, and nothing
  // obliges the vendor to state one. It reads as that, never as a dash.
  const expires = readInstant(o, 'expires_at')
  if (expires.kind === 'unreadable') gaps.push('an expiry date')

  const reject = readText(o, 'reject_reason')

  return {
    cells: [
      id.kind === 'exact' ? String(id.value) : '—',
      customer,
      // A rejected row's reason is the only thing an operator wants beside the
      // type, and there is no column for it — so it rides the type cell rather
      // than being dropped.
      reject === null ? (type ?? '—') : `${type ?? '—'} · ${reject}`,
      statusCell,
      provider ?? '—',
      providerRef ?? '— none',
      identifierCell(o, gaps),
      issuing ?? '— not stated',
      expires.kind === 'at' ? expires.label : expires.kind === 'absent' ? 'no expiry stated' : '—',
      standingCell(o, gaps),
    ],
    gaps,
  }
}

const EVIDENCE_STATUS_COLS: readonly Column[] = [
  ['Status', 'c', '160px'],
  ['Records', 'n', '120px'],
]

function evidenceCounts(raw: unknown, gaps: string[]): Block {
  const counts = asRecord(raw)
  const rows: (readonly Cell[])[] = []
  if (counts === null) {
    gaps.push('the per-status record counts')
  } else {
    for (const [status, value] of Object.entries(counts)) {
      const n = typeof value === 'number' && Number.isSafeInteger(value) ? value : null
      if (n === null) gaps.push('a status count this console could not state')
      rows.push([
        knows(EVIDENCE_TONE, status) ? [status, EVIDENCE_TONE[status] ?? 'a'] : [status, 'a'],
        n === null ? '—' : String(n),
      ])
    }
  }
  return {
    t: 'table', title: 'Every evidence record, by status', src: 'ledger', facets: false,
    cols: EVIDENCE_STATUS_COLS, rows,
    footer: rows.length === 0
      ? 'The per-status population could not be read.'
      : 'Over the whole table including the statuses at zero, never over the page above.',
  }
}

const EVIDENCE_MISSING: readonly string[] = [
  'the SHA-256 column — the identifier digest is not served and will not be; what is shown is whether the vendor supplied one and under which key version',
  'the document itself — this console renders no document, and the files stay at the vendor',
]

export function composeEvidenceQueue(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Evidence review', 'The evidence queue could not be read',
      'No record below is known — that is different from all submitted evidence having been assessed.',
      read, EVIDENCE_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const list = o === null ? null : asArray(o['evidence'])
  if (o === null || list === null) {
    return unread(
      'Evidence review', 'The evidence queue could not be read',
      'The queue returned a shape this console does not know.',
      { ok: false, reason: 'The queue returned a shape this console does not know.', decisionRef: null, refused: false },
      EVIDENCE_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let superseded = 0
  for (const raw of list) {
    const row = readEvidence(raw)
    if (row === null) unreadable += 1
    else {
      rows.push(row.cells)
      gaps.push(...row.gaps)
      const record = asRecord(raw)
      if (record !== null && readBool(record, 'superseded') === true) superseded += 1
    }
  }

  const hasMore = readBool(o, 'has_more') === true

  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'No evidence record on this page. Expiring evidence appears on Re-verification due, not here.'
        : `No row could be read: ${String(unreadable)} came back in a shape this console does not know. This is an unread queue, not an empty one.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'record' : 'records'} shown${hasMore ? ' · the backend caps the page, so this is not the whole queue' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read at all`}`

  const blocks: Block[] = [
    {
      t: 'table', title: 'Evidence records', src: 'ledger', facets: false,
      cols: EVIDENCE_COLS, rows, footer,
    },
    evidenceCounts(o['counts'], gaps),
  ]

  // What the queue's DEPTH means, in the backend's own words. Nothing disposes
  // of an evidence row and each vendor delivery inserts a new one, so a growing
  // queue here is arithmetic rather than a team falling behind — and the two
  // have opposite remedies. There is deliberately no dismiss, archive or
  // resolve control, because no route disposes of one of these rows: a control
  // with nothing behind it is fabricated capability, which is the control-side
  // twin of a fabricated figure.
  const backlog = disclosure(
    o, 'backlog_note', 'Backlog',
    superseded === 0
      ? 'What a queue depth here means'
      : `${String(superseded)} row${superseded === 1 ? ' is' : 's are'} already superseded`,
    'a',
  )
  if (backlog !== null) blocks.push(backlog)

  const missing = [...EVIDENCE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} evidence row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: EVIDENCE_SOURCE, authority: AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Evidence review',
    subject: `onboarding_evidence · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

// ===========================================================================
// onb-emi — the three states the institution has a say in
// ===========================================================================

/**
 * The columns, from what this response carries.
 *
 * The corpus draws Customer / Submitted / EMI reference / State / Age / Our
 * IDV. `Our IDV` is gone: there is no `our_state` on this response, and joining
 * the sibling read to fill it would be this console inventing a join and then
 * owning the disagreement when both pages are open. `Age` is gone with it —
 * this read sends instants and no elapsed figure.
 *
 * `Decision` is new and is law 13: the reference an operator quotes when a
 * customer asks why they were refused, in a monospace column with a copy
 * affordance.
 */
const ACCEPTANCE_COLS: readonly Column[] = [
  ['Customer', 's', 'minmax(180px,1.4fr)'],
  ['Kind', 'c', '104px'],
  ['Acceptance', 'c', '150px'],
  ['Waiting on', 'c', '128px'],
  ['Institution reference', 'm', 'minmax(180px,1.2fr)'],
  ['Submitted', 'm', '150px'],
  ['In state since', 'm', '150px'],
  ['Decision', 'm', '150px'],
  ['Blocking', 'x', 'minmax(190px,1.3fr)'],
]

function readAcceptance(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const customer = customerCell(o, gaps)
  const kind = readText(o, 'kind')
  if (kind === null) gaps.push('an applicant kind')

  const state = acceptanceCell(o, gaps)
  const waiting = waitingCell(o, gaps)

  const emiRef = readText(o, 'emi_subject_ref')

  // From the AUDIT LOG rather than from a column, and the EARLIEST entry: a
  // resubmission after `more_info_required` must not move the date the
  // institution first received the application, which is what a turnaround is
  // measured from. A null means no submission was ever recorded — which is the
  // opposite of a blank meaning "not submitted yet", so it says so in words.
  const submitted = readInstant(o, 'submitted_at')
  if (submitted.kind === 'unreadable') gaps.push('a submission timestamp')

  // Carried BESIDE the submission moment rather than instead of it. While a
  // customer sits in `in_review` the two are the same instant; on exactly the
  // rejected row the design draws, `state_entered_at` has already moved on.
  const entered = readInstant(o, 'state_entered_at')
  if (entered.kind !== 'at') gaps.push('when the current state began')

  // Law 13. A rejection with no reference leaves an operator nothing to put in
  // front of the customer who is asking why, and that absence is stated rather
  // than drawn as a blank.
  const decision = readText(o, 'decision_ref')
  const reason = readText(o, 'acceptance_reason')
  const blocking = readText(o, 'blocking')
  if (blocking === null) gaps.push('the sentence saying what the row waits for')

  return {
    cells: [
      customer,
      kind === null ? ['—', 'a'] : [kind, 'n'],
      state,
      waiting,
      emiRef ?? '— none issued',
      submitted.kind === 'at' ? submitted.label
        : submitted.kind === 'absent' ? 'never recorded' : '—',
      entered.kind === 'at' ? entered.label : '—',
      decision ?? '— none quoted',
      reason === null ? (blocking ?? '—') : `${blocking ?? '—'} · ${reason}`,
    ],
    gaps,
  }
}

const ACCEPTANCE_MISSING: readonly string[] = [
  'our own identity-check state — it is not on this response; it is on the application queue, and joining the two here would invent a join this console would then have to defend',
  'an age — this read sends a submission instant and a state-entered instant and no elapsed figure',
]

export function composeInstitutionAcceptance(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'EMI acceptance queue', 'The acceptance queue could not be read',
      'No handoff below is known — that is different from every verified application having an acceptance record.',
      read, ACCEPTANCE_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const apps = o === null ? null : asArray(o['applications'])
  if (o === null || apps === null) {
    return unread(
      'EMI acceptance queue', 'The acceptance queue could not be read',
      'The queue returned a shape this console does not know.',
      { ok: false, reason: 'The queue returned a shape this console does not know.', decisionRef: null, refused: false },
      ACCEPTANCE_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let unquoted = 0
  for (const raw of apps) {
    const row = readAcceptance(raw)
    if (row === null) unreadable += 1
    else {
      rows.push(row.cells)
      gaps.push(...row.gaps)
      const record = asRecord(raw)
      if (record !== null
        && readText(record, 'acceptance') === 'rejected'
        && readText(record, 'decision_ref') === null) unquoted += 1
    }
  }

  const hasMore = readBool(o, 'has_more') === true

  // This queue is scoped to the three states the institution has any say in.
  // `pending` and `more_info_required` are ours and the customer's and are on
  // the application queue — that is the difference between the two reads, and
  // the footer says it so nobody works the wrong one.
  const footer = rows.length === 0
    ? (unreadable === 0
        ? 'Nothing is with the institution. This queue is in_review, accepted and rejected only — pending and more_info_required are ours and are on the application queue.'
        : `No row could be read: ${String(unreadable)} came back in a shape this console does not know. This is an unread queue, not an empty one.`)
    : `${String(rows.length)} ${rows.length === 1 ? 'application' : 'applications'} · in_review, accepted and rejected only${hasMore ? ' · the backend caps the page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read at all`}`

  const blocks: Block[] = [{
    t: 'table',
    title: 'Applications with the institution',
    // `provider` in the corpus, and it is right: the verdict in the acceptance
    // column is the institution's, mirrored, and not a figure we computed.
    src: 'provider',
    facets: false,
    cols: ACCEPTANCE_COLS,
    rows,
    footer,
  }]

  // Law 13, said where it bites: a refused applicant whose row carries no
  // reference is one a support agent cannot answer for.
  if (unquoted > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Authority',
      title: `${String(unquoted)} rejected application${unquoted === 1 ? '' : 's'} with no decision reference`,
      body: 'There is nothing on those rows to quote back to the customer or to put in an escalation, and this console will not invent one.',
      ref: 'law 13 · decision_ref absent on a refusal',
    })
  }

  const missing = [...ACCEPTANCE_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} acceptance row(s) this console could not read`)

  return {
    blocks,
    provenance: {
      kind: 'partial', source: ACCEPTANCE_SOURCE, authority: AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'EMI acceptance queue',
    subject: `customers · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

// ===========================================================================
// onb-reverify — who is due, and on what basis
// ===========================================================================

/**
 * Two bases, and they are not interchangeable.
 *
 * `evidence_expiry` is the identity vendor's own date, taken from a verified
 * document. `cadence_from_approval` is OUR periodic review, used only where the
 * vendor stated no expiry — which is most of the population, because nothing
 * obliges the vendor to state one. Presented as one column of dates, an
 * operator would read a policy interval as a document expiry, and the two are
 * argued for completely differently at an audit.
 */
const BASIS_LABEL: Readonly<Record<string, string>> = {
  evidence_expiry: 'the document’s own expiry',
  cadence_from_approval: 'our periodic review',
}

/**
 * The columns.
 *
 * The corpus draws Customer / Reason / Due / Days / Requested / If overdue.
 * `Requested` is gone — it is false on every row by construction, so it
 * distinguishes nobody, and drawn per row it shows an outreach process that
 * does not exist. `If overdue` is gone — no policy row ties an overdue
 * re-verification to an account state, and inventing one here would be this
 * console writing compliance policy.
 *
 * `Basis` and `Computed from` are new and are the point of the endpoint: how
 * the date was reached, and the instant it was reached from, so an operator can
 * check the arithmetic rather than trust it.
 */
const REVERIFY_COLS: readonly Column[] = [
  ['Customer', 's', 'minmax(180px,1.4fr)'],
  ['Basis', 'c', '170px'],
  ['Subject', 'x', 'minmax(160px,1.2fr)'],
  ['Computed from', 'm', '150px'],
  ['Due', 'm', '150px'],
  ['Days', 'n', '92px'],
  ['Cadence cited', 'm', '150px'],
]

function readDue(raw: unknown): RowRead | null {
  const o = asRecord(raw)
  if (o === null) return null
  const gaps: string[] = []

  const customer = customerCell(o, gaps)

  const basis = readText(o, 'basis')
  if (basis === null) gaps.push('the basis a due date was reached by')
  const basisCell: ChipCell = basis === null
    ? ['unstated', 'a']
    : knows(BASIS_LABEL, basis)
      ? [BASIS_LABEL[basis] ?? basis, 'n']
      : [basis, 'a']
  if (basis !== null && !knows(BASIS_LABEL, basis)) {
    gaps.push('a basis this console recognises')
  }

  // `evidence_type` is absent on a cadence row, where there is no document.
  const kind = readText(o, 'subject_kind')
  const evidenceType = readText(o, 'evidence_type')
  const reason = readText(o, 'reason_code')
  const subject = evidenceType ?? kind ?? '—'

  const basisAt = readInstant(o, 'basis_at')
  if (basisAt.kind !== 'at') gaps.push('the instant a due date was computed from')
  const dueAt = readInstant(o, 'due_at')
  if (dueAt.kind !== 'at') gaps.push('a due date')

  // Server-computed, and negative when overdue — so no ambient clock here, and
  // the sign is the whole information.
  const days = readInt(o, 'days_until')
  if (days.kind !== 'exact') gaps.push('a days-until figure')
  const overdue = readBool(o, 'overdue')
  if (overdue === null) gaps.push('whether a row is overdue')

  // All three or none: a cadence cited without its version resolves through
  // whatever the policy says today, which is the citation law 6 refuses. An
  // expiry row cites none at all, because no policy of ours produced its date.
  const cadence = asRecord(o['cadence'])
  let cadenceLabel = basis === 'evidence_expiry' ? 'none — the vendor set this date' : '— none cited'
  if (cadence !== null) {
    const id = readInt(cadence, 'id')
    const version = readInt(cadence, 'version')
    const months = readInt(cadence, 'months')
    if (id.kind !== 'exact' || version.kind !== 'exact' || months.kind !== 'exact') {
      gaps.push('a cadence citation this console could not state in full')
      cadenceLabel = '— partly read'
    } else {
      cadenceLabel = `#${String(id.value)} v${String(version.value)} · ${String(months.value)}m`
    }
  }

  return {
    cells: [
      customer,
      basisCell,
      reason === null ? subject : `${subject} · ${reason}`,
      basisAt.kind === 'at' ? basisAt.label : '—',
      dueAt.kind === 'at' ? dueAt.label : '—',
      days.kind === 'exact'
        ? (overdue === true ? `${String(days.value)} · overdue` : String(days.value))
        : '—',
      cadenceLabel,
    ],
    gaps,
  }
}

const REVERIFY_MISSING: readonly string[] = [
  'whether anybody has been asked to re-verify — nothing in this build sends a reminder or a request, so “not yet” is true of every row and distinguishes none of them',
  'what happens if a re-verification goes overdue — no policy ties one to an account state, and this console does not invent the tie',
]

export function composeReverificationDue(read: Attempt, at: Date): ScreenData {
  if (!read.ok) {
    return unread(
      'Re-verification due', 'The re-verification schedule could not be read',
      'Nobody below is known to be due — and that is different from nobody being due.',
      read, REVERIFICATION_SOURCE,
    )
  }

  const o = asRecord(read.payload)
  const due = o === null ? null : asArray(o['due'])
  if (o === null || due === null) {
    return unread(
      'Re-verification due', 'The re-verification schedule could not be read',
      'The schedule returned a shape this console does not know.',
      { ok: false, reason: 'The schedule returned a shape this console does not know.', decisionRef: null, refused: false },
      REVERIFICATION_SOURCE,
    )
  }

  const gaps: string[] = []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  let overdue = 0
  for (const raw of due) {
    const row = readDue(raw)
    if (row === null) unreadable += 1
    else {
      rows.push(row.cells)
      gaps.push(...row.gaps)
      const record = asRecord(raw)
      if (record !== null && readBool(record, 'overdue') === true) overdue += 1
    }
  }

  const horizon = readInt(o, 'horizon_days')
  if (horizon.kind !== 'exact') gaps.push('the horizon this list was bounded by')
  const unschedulable = readInt(o, 'unschedulable')
  if (unschedulable.kind !== 'exact') gaps.push('how many approved customers cannot be dated at all')
  const hasMore = readBool(o, 'has_more') === true

  // THE FIELD THAT DECIDES WHAT AN EMPTY SCREEN MEANS. A null `last_computed_at`
  // means the schedule has NEVER been computed, which renders as the same empty
  // list as "nobody is due" and is the opposite fact — so a console that drew
  // an empty queue without reading this would tell an operator their work was
  // done when nothing had run.
  const computed = readInstant(o, 'last_computed_at')
  const neverComputed = computed.kind === 'absent'
  if (computed.kind === 'unreadable') gaps.push('when the schedule was last computed')

  const window = horizon.kind === 'exact' ? `${String(horizon.value)} days` : 'the requested window'

  const footer = rows.length === 0
    ? (neverComputed
        ? 'THE SCHEDULE HAS NEVER BEEN COMPUTED. This list is empty because nothing has run, not because nobody is due — the two look identical and mean opposite things.'
        : unreadable === 0
          ? `Nobody is due within ${window}. The schedule was computed and this was read to confirm it.`
          : `No row could be read: ${String(unreadable)} came back in a shape this console does not know.`)
    : `${String(rows.length)} due within ${window}${overdue === 0 ? '' : ` · ${String(overdue)} already overdue`}${hasMore ? ' · the backend caps the page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} row(s) could not be read at all`}`

  const blocks: Block[] = [{
    t: 'table', title: 'Re-verification schedule', src: 'ledger', facets: false,
    cols: REVERIFY_COLS, rows, footer,
  }]

  // What `overdue` does and does not mean, in the backend's own words. The
  // designed screen renders "restricted (receive-only)" as the consequence and
  // no policy row anywhere backs it — this is the sentence that stops an
  // operator inferring one, and it is why there is no restrict control here.
  const consequence = disclosure(
    o, 'consequence_note', 'Overdue', 'What overdue means here', 'n',
  )
  if (consequence !== null && rows.length > 0) blocks.push(consequence)

  // And that nobody has been asked. `requested` is false on every row by
  // construction, so the fact belongs to the whole list rather than to a column.
  const solicitation = disclosure(
    o, 'solicitation_note', 'Outreach', 'Nobody has been asked to re-verify', 'a',
  )
  if (solicitation !== null && rows.length > 0) blocks.push(solicitation)

  // The dates are only as fresh as the last pass, and a never-run pass is not
  // an empty queue. Said on a populated screen too, because a cadence
  // superseded five minutes ago is not reflected in the rows above.
  if (neverComputed) {
    blocks.push({
      t: 'note', tone: 'r', kicker: 'Freshness',
      title: 'Nothing has computed this schedule',
      body: 'No pass has ever run, so this list is empty by default rather than by finding. Nobody has been checked, and nobody here is up to date.',
      ref: 'last_computed_at is null',
    })
  } else if (computed.kind === 'at') {
    blocks.push({
      t: 'note', tone: 'n', kicker: 'Freshness',
      title: `Computed ${computed.label}`,
      body: 'The dates are as old as that pass. A cadence superseded since is not reflected here yet.',
      ref: 'last_computed_at',
    })
  }

  // Approved customers with neither a stated expiry nor a recorded approval
  // instant — every customer approved before onboarding transitions began being
  // recorded. They are absent from the list above and they are NOT up to date,
  // and their approval moment is deliberately not reconstructed from
  // `updated_at`, which would produce a plausible date wrong by however long
  // they spent in retry.
  if (unschedulable.kind === 'exact' && unschedulable.value > 0) {
    blocks.push({
      t: 'note', tone: 'a', kicker: 'Unschedulable',
      title: `${String(unschedulable.value)} approved customer${unschedulable.value === 1 ? '' : 's'} cannot be dated at all`,
      body: 'They have neither a stated document expiry nor a recorded approval instant, so no due date exists for them. They are absent from the list above and they are not up to date.',
      ref: 'unschedulable',
    })
  }

  const missing = [...REVERIFY_MISSING, ...summarise(gaps)]
  if (unreadable > 0) missing.push(`${String(unreadable)} schedule row(s) this console could not read`)
  if (neverComputed) missing.push('every due date — the schedule has never been computed')

  return {
    blocks,
    provenance: {
      kind: 'partial', source: REVERIFICATION_SOURCE, authority: AUTHORITY,
      readAt: receipt(at), missing,
    },
    title: 'Re-verification due',
    subject: `reverification schedule · ${String(rows.length)} ${rows.length === 1 ? 'row' : 'rows'}`,
  }
}

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export const loadApplicationQueue: ScreenLoader = async (api) =>
  composeApplicationQueue(
    await attempt(() => api.get<unknown>(APPLICATIONS_PATH), 'The onboarding application queue'),
    new Date(),
  )

export const loadEvidenceQueue: ScreenLoader = async (api) =>
  composeEvidenceQueue(
    await attempt(() => api.get<unknown>(EVIDENCE_PATH), 'The onboarding evidence queue'),
    new Date(),
  )

export const loadInstitutionAcceptance: ScreenLoader = async (api) =>
  composeInstitutionAcceptance(
    await attempt(() => api.get<unknown>(ACCEPTANCE_PATH), 'The institution acceptance queue'),
    new Date(),
  )

export const loadReverificationDue: ScreenLoader = async (api) =>
  composeReverificationDue(
    await attempt(() => api.get<unknown>(REVERIFICATION_PATH), 'The re-verification schedule'),
    new Date(),
  )
